/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { IconX, IconUser, IconPackage } from "@tabler/icons-react";
import React, { useEffect, useState } from "react";
import { OrderFormData, PAYMENT_TYPE_OPTIONS } from "../../../types/types";
import ButtonStyled from "../../../components/common/ButtonStyled";
import {
  FieldErrors,
  UseFormClearErrors,
  UseFormRegister,
  UseFormSetValue,
} from "react-hook-form";
import NewinputField from "../../../components/NewInput";
import NewSelectInput from "../../../components/NewSelectInput";
import {
  useDistrictFetchUpdateData,
  useFetchCountriesAllData,
  useFetchCountryCitiesUpdateAllData,
} from "../../../apis/locationApis";

type Props = {
  open: boolean;
  onClose: () => void;
  order: any;
  register: UseFormRegister<OrderFormData>;
  errors: FieldErrors<OrderFormData>;
  setValue: UseFormSetValue<OrderFormData>;
  watch: any;
  clearErrors: UseFormClearErrors<OrderFormData>;
  handleSubmit: React.MouseEventHandler<HTMLButtonElement>;
  isPending: boolean;
  inPlace?: boolean;
  height?: number;
};

const SectionHeader = ({ title }: { icon?: any; title: string }) => (
  <div className="flex items-center gap-2 pb-1 mb-2">
    <p className="text-[#212121]/80 uppercase text-[11px] font-bold tracking-wide">
      {title}
    </p>
  </div>
);

const EditOrderModal = ({
  open,
  onClose,
  order,
  register,
  errors,
  setValue,
  watch,
  clearErrors,
  handleSubmit,
  isPending,
  inPlace = false,
  height,
}: Props) => {
  const [editMode, setEditMode] = useState(false);

  // API data
  const { data: countries = [] } = useFetchCountriesAllData();
  const { data: senderCities = [], refetch: fetchSenderCities } =
    useFetchCountryCitiesUpdateAllData(watch("sender.country"));
  const { data: senderDistricts = [], refetch: fetchSenderDistricts } =
    useDistrictFetchUpdateData(watch("sender.city"));
  const { data: receiverCities = [], refetch: fetchReceiverCities } =
    useFetchCountryCitiesUpdateAllData(watch("receiver.country"));
  const { data: receiverDistricts = [], refetch: fetchReceiverDistricts } =
    useDistrictFetchUpdateData(watch("receiver.city"));

  useEffect(() => {
    if (order?.receiverCountry) {
      fetchReceiverCities();
    }
  }, [order?.receiverCountry]);

  useEffect(() => {
    if (order?.receiverCity) {
      fetchReceiverDistricts();
    }
  }, [order?.receiverCity]);

  useEffect(() => {
    if (order?.senderCountry) {
      fetchSenderCities();
    }
  }, [order?.senderCountry]);

  useEffect(() => {
    if (order?.senderCity) {
      fetchSenderDistricts();
    }
  }, [order?.senderCity]);

  useEffect(() => {
    if (order) {
      const senderAddr = order?.senderNationalAddress || order?.senderShortAddress || "";
      const receiverAddr = order?.receiverNationalAddress || order?.receiverShortAddress || "";

      setValue("sender.name", order?.senderName);
      setValue("sender.mobile", order?.senderMobile);
      setValue("sender.email", order?.senderEmail);
      setValue("sender.country", order?.senderCountry);
      setValue("sender.city", order?.senderCity);
      setValue("sender.district", order?.senderDistrict);
      setValue("sender.zipCode", order?.senderZipCode);
      setValue("sender.latitude", order?.senderLatitude);
      setValue("sender.longitude", order?.senderLongitude);
      setValue("sender.address", order?.senderAddress);
      setValue("sender.nationalAddress", senderAddr);
      setValue("sender.shortAddress", senderAddr);
      setValue("receiver.name", order?.receiverName);
      setValue("receiver.mobile", order?.receiverMobile);
      setValue("receiver.email", order?.receiverEmail);
      setValue("receiver.country", order?.receiverCountry);
      setValue("receiver.city", order?.receiverCity);
      setValue("receiver.district", order?.receiverDistrict);
      setValue("receiver.zipCode", order?.receiverZipCode);
      setValue("receiver.latitude", order?.receiverLatitude);
      setValue("receiver.longitude", order?.receiverLongitude);
      setValue("receiver.address", order?.receiverAddress);
      setValue("receiver.nationalAddress", receiverAddr);
      setValue("receiver.shortAddress", receiverAddr);
      setValue("shipment.paymentType", order?.paymentType);
      setValue("shipment.codAmount", order?.codAmount);
      setValue("shipment.numberOfPieces", order?.noOfPieces);
      setValue("shipment.totalWeight", order?.totalWeight);
    }
  }, [order]);

  if (!open) return null;

  const formContent = (
    <div className="space-y-4">
      {/* RECEIVER INFORMATION SECTION */}
      <div className="group rounded-2xl p-4 transition-all duration-300 space-y-3">
        <SectionHeader title="Receiver Details" />

        <div className="grid grid-cols-2 gap-3">
          <NewinputField
            className="col-span-2"
            name="receiver.name"
            type="text"
            title="Name"
            register={register}
            error={errors.receiver?.name}
            validation={{ required: "Receiver name is required" }}
          />
          <NewinputField
            className="w-full"
            name="receiver.mobile"
            type="number"
            title="Mobile"
            register={register}
            error={errors.receiver?.mobile}
            validation={{ required: "Receiver mobile is required" }}
          />

          <NewinputField
            className="w-full"
            name="receiver.email"
            type="text"
            title="Email"
            register={register}
            error={errors.receiver?.email}
            validation={{ required: "Receiver email is required" }}
          />
          <NewSelectInput
            className="w-full"
            name="receiver.country"
            label="Country"
            placeholder="Select Country"
            selectOptions={countries.map((country: any) => ({
              id: country.id,
              value: country.id,
              text: country.name,
            }))}
            register={register}
            clearErrors={clearErrors}
            validation={{ required: "Receiver country is required" }}
            disabled={!countries.length}
            value={watch("receiver.country")}
            setValue={(val: string) => {
              const country = countries.find((c: any) => c.id === val);
              setValue("receiver.country", val);
              setValue("receiver.countryName", country?.name || "");
              setValue("receiver.city", "");
              setValue("receiver.cityName", "");
              setValue("receiver.district", "");
              setValue("receiver.districtName", "");
            }}
          />

          <div className="flex flex-col gap-1 w-full">
            <label className="text-[#1E293B] text-xs whitespace-nowrap">
              Short Add<span className="text-red-600">*</span>
            </label>
            <input
              {...register("receiver.nationalAddress", {
                required: "Receiver national address is required",
              })}
              className="w-full rounded-lg border resize-none border-[#D8D8D8]/20 px-3 py-[10px] outline-none text-xs text-[#1E1E1E] focus:border-[#FBD037]"
            />
            {errors.receiver?.nationalAddress && (
              <span className="text-red-500 text-xs block mt-0.5">
                {errors.receiver?.nationalAddress?.message}
              </span>
            )}
          </div>

          <NewSelectInput
            className="w-full"
            name="receiver.city"
            label="City"
            placeholder="Select City"
            selectOptions={receiverCities.map((city: any) => ({
              id: city.id,
              value: city.id,
              text: city.name,
            }))}
            clearErrors={clearErrors}
            register={register}
            validation={{ required: "Receiver city is required" }}
            disabled={!receiverCities.length}
            value={watch("receiver.city")}
            setValue={(val: string) => {
              const city = receiverCities?.find((c: any) => c.id === val);
              setValue("receiver.city", val);
              setValue("receiver.cityName", city?.name || "");
              setValue("receiver.district", "");
              setValue("receiver.districtName", "");
            }}
          />
          <NewSelectInput
            className="w-full"
            name="receiver.district"
            label="District"
            placeholder="Select District"
            selectOptions={receiverDistricts.map((district: any) => ({
              id: district.id,
              value: district.id,
              text: district.name,
            }))}
            clearErrors={clearErrors}
            register={register}
            validation={{ required: "Receiver district is required" }}
            disabled={!receiverDistricts.length}
            value={watch("receiver.district")}
            setValue={(val: string) => {
              const district = receiverDistricts?.find(
                (d: any) => d.id === val,
              );
              setValue("receiver.district", val);
              setValue("receiver.districtName", district?.name || "");
            }}
          />
          <NewinputField
            className="w-full"
            name="receiver.zipCode"
            type="text"
            title="Zip Code"
            register={register}
            error={errors.receiver?.zipCode}
            validation={{ required: "Receiver zip code is required" }}
          />
          <NewinputField
            className="w-full"
            name="receiver.latitude"
            type="number"
            title="Latitude"
            step="any"
            inputMode="decimal"
            register={register}
            error={errors.receiver?.latitude}
            validation={{ required: "Receiver latitude is required" }}
          />

          <NewinputField
            className="w-full"
            name="receiver.longitude"
            type="number"
            title="Longitude"
            step="any"
            inputMode="decimal"
            register={register}
            error={errors.receiver?.longitude}
            validation={{ required: "Receiver longitude is required" }}
          />
          <div className="flex flex-col gap-1 w-full">
            <label className="text-[#1E293B] text-xs whitespace-nowrap">
              Address<span className="text-red-600">*</span>
            </label>
            <input
              {...register("receiver.address", {
                required: "Receiver address is required",
              })}
              className="w-full rounded-lg border resize-none border-[#D8D8D8]/20 px-3 py-[10px] outline-none text-xs text-[#1E1E1E] focus:border-[#FBD037]"
            />
            {errors.receiver?.address && (
              <span className="text-red-500 text-xs block mt-0.5">
                {errors.receiver?.address?.message}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* SENDER INFORMATION SECTION */}
      <div className="group rounded-2xl p-4 transition-all duration-300 space-y-3">
        <SectionHeader title="Sender Details" />

        <div className="grid grid-cols-2 gap-3">
          <NewinputField
            className="col-span-2"
            name="sender.name"
            type="text"
            title="Name"
            register={register}
            error={errors.sender?.name}
            validation={{ required: "Sender name is required" }}
          />
          <NewinputField
            className="w-full"
            name="sender.mobile"
            type="number"
            title="Mobile"
            register={register}
            error={errors.sender?.mobile}
            validation={{ required: "Sender mobile is required" }}
          />

          <NewinputField
            className="w-full"
            name="sender.email"
            type="text"
            title="Email"
            register={register}
            error={errors.sender?.email}
            validation={{ required: "Sender email is required" }}
          />

          <NewSelectInput
            className="w-full"
            name="sender.country"
            label="Country"
            placeholder="Select Country"
            selectOptions={countries.map((country: any) => ({
              id: country.id,
              value: country.id,
              text: country.name,
            }))}
            register={register}
            clearErrors={clearErrors}
            validation={{ required: "Sender country is required" }}
            disabled={!countries.length}
            value={watch("sender.country")}
            setValue={(val: string) => {
              const country = countries.find((c: any) => c.id === val);
              setValue("sender.country", val);
              setValue("sender.countryName", country?.name || "");
              setValue("sender.city", "");
              setValue("sender.cityName", "");
              setValue("sender.district", "");
              setValue("sender.districtName", "");
            }}
          />
          <div className="flex flex-col gap-1 w-full">
            <label className="text-[#1E293B] text-xs whitespace-nowrap">
              Short Address<span className="text-red-600">*</span>
            </label>
            <input
              {...register("sender.nationalAddress", {
                required: "Sender national address is required",
              })}
              className="w-full rounded-xl border border-[#D8D8D8]/20 px-3 py-[10px] outline-none text-xs text-[#1E1E1E] focus:border-[#FBD037]"
            />
            {errors.sender?.nationalAddress && (
              <span className="text-red-500 text-xs block mt-0.5">
                {errors.sender?.nationalAddress?.message}
              </span>
            )}
          </div>
          <NewSelectInput
            className="w-full"
            name="sender.city"
            label="City"
            placeholder="Select City"
            selectOptions={senderCities.map((city: any) => ({
              id: city.id,
              value: city.id,
              text: city.name,
            }))}
            clearErrors={clearErrors}
            register={register}
            validation={{ required: "Sender city is required" }}
            disabled={!senderCities.length}
            value={watch("sender.city")}
            setValue={(val: string) => {
              const city = senderCities?.find((c: any) => c.id === val);
              setValue("sender.city", val);
              setValue("sender.cityName", city?.name || "");
              setValue("sender.district", "");
              setValue("sender.districtName", "");
            }}
          />
          <NewSelectInput
            className="w-full"
            name="sender.district"
            label="District"
            placeholder="Select District"
            selectOptions={senderDistricts.map((district: any) => ({
              id: district.id,
              value: district.id,
              text: district.name,
            }))}
            clearErrors={clearErrors}
            register={register}
            validation={{ required: "Sender district is required" }}
            disabled={!senderDistricts.length}
            value={watch("sender.district")}
            setValue={(val: string) => {
              const district = senderDistricts?.find((d: any) => d.id === val);
              setValue("sender.district", val);
              setValue("sender.districtName", district?.name || "");
            }}
          />
          <NewinputField
            className="w-full"
            name="sender.zipCode"
            type="text"
            title="Zip Code"
            register={register}
            error={errors.sender?.zipCode}
            validation={{ required: "Sender zip code is required" }}
          />
          <NewinputField
            className="w-full"
            name="sender.latitude"
            type="number"
            title="Latitude"
            step="any"
            inputMode="decimal"
            register={register}
            error={errors.sender?.latitude}
          />

          <NewinputField
            className="w-full"
            name="sender.longitude"
            type="number"
            title="Longitude"
            step="any"
            inputMode="decimal"
            register={register}
            error={errors.sender?.longitude}
          />

          <div className="flex flex-col gap-1 w-full">
            <label className="text-[#1E293B] text-xs whitespace-nowrap">
              Address<span className="text-red-600">*</span>
            </label>
            <input
              {...register("sender.address", {
                required: "Sender address is required",
              })}
              className="w-full rounded-xl border border-[#D8D8D8]/20 px-3 py-[10px] outline-none text-xs text-[#1E1E1E] focus:border-[#FBD037]"
            />
            {errors.sender?.address && (
              <span className="text-red-500 text-xs block mt-0.5">
                {errors.sender?.address?.message}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* SHIPMENT DETAILS SECTION */}
      <div className="group rounded-2xl p-4 transition-all duration-300 space-y-3">
        <SectionHeader title="Shipment Details" />

        <div className="grid grid-cols-2 gap-3">
          <NewSelectInput
            className="w-full"
            name="shipment.paymentType"
            label="Payment"
            placeholder="Select Payment Type"
            selectOptions={PAYMENT_TYPE_OPTIONS}
            clearErrors={clearErrors}
            register={register}
            value={watch("shipment.paymentType")}
            setValue={(val: string) => {
              setValue("shipment.paymentType", val);
              if (val !== "COD") {
                setValue("shipment.codAmount", "");
              }
            }}
            validation={{ required: "Payment Type is required" }}
          />

          {watch("shipment.paymentType") === "COD" ? (
            <NewinputField
              className="w-full"
              name="shipment.codAmount"
              type="number"
              title="Amount"
              register={register}
              value={watch("shipment.codAmount")}
              error={errors.shipment?.codAmount}
              validation={{
                required: "COD amount is required",
                min: { value: 0.01, message: "Must be > 0" },
                validate: (v) => parseFloat(v) > 0 || "Must be > 0",
              }}
            />
          ) : (
            <div />
          )}
          <NewinputField
            name="shipment.numberOfPieces"
            className="w-full"
            type="number"
            title="Pieces *"
            register={register}
            error={errors.shipment?.numberOfPieces}
            value={watch("shipment.numberOfPieces")}
            validation={{
              required: "No of pieces is required",
              min: { value: 1, message: "Must be at least 1" },
            }}
          />

          <NewinputField
            className="w-full"
            name="shipment.totalWeight"
            type="text"
            title="Weight (kg) *"
            register={register}
            error={errors.shipment?.totalWeight}
            value={watch("shipment.totalWeight")}
            validation={{
              required: "Total weight is required",
              pattern: {
                value: /^\d*\.?\d*$/,
                message: "Must be a valid number",
              },
              min: { value: 0.001, message: "Must be > 0" },
              validate: (v) => parseFloat(v) > 0 || "Must be > 0",
            }}
          />
        </div>
      </div>
    </div>
  );

  if (inPlace) {
    return (
      <div className="bg-white border border-slate-200/80 shadow-xl rounded-2xl overflow-hidden flex flex-col h-[calc(100vh-180px)] max-h-[700px]">
        {/* HEADER */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-white shrink-0">
          <div>
            <h3 className="text-base font-bold text-[#212121]">Edit Order</h3>
            <p className="text-xs text-slate-500 font-medium mt-0.5">
              AWB No. {order?.awbNumber || order?.referenceNumber}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <ButtonStyled
              type="button"
              onClick={handleSubmit}
              isPending={isPending}
              variant="outline"
              value="Update"
            />
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-100 transition-colors text-slate-700 bg-slate-50 cursor-pointer"
            >
              <IconX size={16} />
            </button>
          </div>
        </div>

        {/* SCROLLABLE FORM BODY */}
        <div className="w-full flex-1 overflow-y-auto min-h-0 p-5 scrollbar-clean">
          {formContent}
        </div>
      </div>
    );
  }

  return (
    <div className="fixed top-0 left-0 w-full h-screen bg-black/60 z-50 flex items-center justify-end p-4">
      <div className="relative border rounded-lg border-gray-200  rounded-2xl xl:w-5/12 lg:w-6/12 w-11/12 h-[calc(100vh-2rem)] flex flex-col overflow-hidden shadow-2xl">
        {/* HEADER */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#d2d2d2]  shrink-0">
          <div>
            <h3 className="text-lg font-bold text-[#212121]">Edit Order</h3>
            <p className="text-xs text-slate-500 font-medium mt-0.5">
              AWB No. {order?.awbNumber || order?.referenceNumber}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <ButtonStyled
              type="button"
              onClick={handleSubmit}
              isPending={isPending}
              variant="outline"
              value="Update"
            />
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-slate-700 bg-[#F2F2F7] cursor-pointer"
            >
              <IconX size={16} />
            </button>
          </div>
        </div>

        {/* SCROLLABLE FORM BODY */}
        <div className="w-full flex-1 overflow-y-auto min-h-0 p-5 scrollbar-clean">
          {formContent}
        </div>
      </div>
    </div>
  );
};

export default EditOrderModal;
